import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DOCUMENT_TEMPLATES } from "../content/templates";
import { DOCUMENT_SNIPPETS } from "../content/snippets";
import { applyMetaPlaceholders } from "../lib/placeholders";

const IMAGE_MAX_BYTES = 1.5 * 1024 * 1024;

function slugFilePart(s, fallback) {
  const slug =
    s
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]+/g, "")
      .replace(/\s+/g, "-")
      .slice(0, 48) || fallback;
  return slug;
}

export function EditorFileBar({
  meta,
  markdown,
  setMarkdown,
  insertAtCursor,
  onClearDraft,
  focusMode,
  setFocusMode,
}) {
  const { t } = useTranslation();
  const importRef = useRef(null);
  const imageRef = useRef(null);
  const slugFallback = t("mdwriter.slugFallback");

  const downloadMarkdown = useCallback(() => {
    const base = slugFilePart(meta.documentTitle || "", slugFallback);
    const date = new Date().toISOString().slice(0, 10);
    const name = `${base}-${date}.md`;
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }, [markdown, meta.documentTitle, slugFallback]);

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      if (text && !window.confirm(t("mdwriter.confirmImport"))) {
        return;
      }
      setMarkdown(text);
    };
    reader.readAsText(file, "UTF-8");
  };

  const applyTemplate = (templateBody) => {
    const filled = applyMetaPlaceholders(templateBody, meta);
    if (!window.confirm(t("mdwriter.confirmTemplate"))) {
      return;
    }
    setMarkdown(filled);
  };

  const onImageSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > IMAGE_MAX_BYTES) {
      window.alert(
        t("mdwriter.imageTooLarge", {
          mb: Math.round(IMAGE_MAX_BYTES / 1024 / 1024),
        }),
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      if (!dataUrl) return;
      const alt =
        file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ") ||
        t("mdwriter.imageAltFallback");
      insertAtCursor(`\n\n![${alt}](${dataUrl})\n\n`);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="editor-file-bar" role="toolbar" aria-label={t("mdwriter.fileBarAria")}>
      <input
        ref={importRef}
        type="file"
        accept=".md,.markdown,text/markdown,text/plain"
        className="mdwriter-visually-hidden"
        tabIndex={-1}
        onChange={onImportFile}
      />
      <input
        ref={imageRef}
        type="file"
        accept="image/*"
        className="mdwriter-visually-hidden"
        tabIndex={-1}
        onChange={onImageSelected}
      />

      <div className="editor-file-group">
        <button
          type="button"
          className="btn-tool"
          onClick={() => importRef.current?.click()}
        >
          {t("mdwriter.importMd")}
        </button>
        <button type="button" className="btn-tool" onClick={downloadMarkdown}>
          {t("mdwriter.exportMd")}
        </button>
        <button
          type="button"
          className="btn-tool btn-tool--warn"
          onClick={() => {
            if (window.confirm(t("mdwriter.confirmReset"))) {
              onClearDraft();
            }
          }}
        >
          {t("mdwriter.clearDraft")}
        </button>
      </div>

      <div className="editor-file-group">
        <label className="editor-select-wrap">
          <span className="mdwriter-visually-hidden">{t("mdwriter.loadTemplate")}</span>
          <select
            className="editor-select"
            aria-label={t("mdwriter.loadTemplate")}
            defaultValue=""
            onChange={(e) => {
              const id = e.target.value;
              e.target.value = "";
              const tpl = DOCUMENT_TEMPLATES.find((x) => x.id === id);
              if (tpl) applyTemplate(tpl.body);
            }}
          >
            <option value="" disabled>
              {t("mdwriter.loadTemplatePlaceholder")}
            </option>
            {DOCUMENT_TEMPLATES.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {t(`mdwriter.templates.${tpl.id}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="editor-select-wrap">
          <span className="mdwriter-visually-hidden">{t("mdwriter.insertSnippet")}</span>
          <select
            className="editor-select"
            aria-label={t("mdwriter.insertSnippet")}
            defaultValue=""
            onChange={(e) => {
              const id = e.target.value;
              e.target.value = "";
              const s = DOCUMENT_SNIPPETS.find((x) => x.id === id);
              if (s) insertAtCursor(s.body);
            }}
          >
            <option value="" disabled>
              {t("mdwriter.insertSnippetPlaceholder")}
            </option>
            {DOCUMENT_SNIPPETS.map((s) => {
              const hint = t(`mdwriter.snippets.${s.id}.hint`);
              return (
                <option key={s.id} value={s.id}>
                  {t(`mdwriter.snippets.${s.id}.label`)}
                  {hint ? ` — ${hint}` : ""}
                </option>
              );
            })}
          </select>
        </label>

        <button
          type="button"
          className="btn-tool"
          onClick={() => imageRef.current?.click()}
        >
          {t("mdwriter.insertImage")}
        </button>
      </div>

      <div className="editor-file-group editor-file-group--grow">
        <label className="editor-focus-label">
          <span className="editor-focus-text">{t("mdwriter.view")}</span>
          <select
            className="editor-select editor-select--narrow"
            value={focusMode}
            onChange={(e) => setFocusMode(e.target.value)}
            aria-label={t("mdwriter.layoutAria")}
          >
            <option value="split">{t("mdwriter.layoutSplit")}</option>
            <option value="editor">{t("mdwriter.layoutEditor")}</option>
            <option value="preview">{t("mdwriter.layoutPreview")}</option>
          </select>
        </label>
      </div>
    </div>
  );
}
